import { DeviceLabels, MeetingManager } from "amazon-chime-sdk-component-library-react";
import { MeetingSessionConfiguration } from "amazon-chime-sdk-js";
import { AttendeeResponse } from "amazon-chime-sdk-component-library-react/lib/providers/MeetingProvider/types";

import { MeetingJoinType } from "../types"
import { createCustomToken, CustomAuthoriserData, getUrl } from "./service-utils";

/**
 * Sends a join request to the backend to get meeting and attendee info
 * @param meetingId - The id of the meeting to join
 * @param attendeeId - The id of the attendee joining the meeting
 * @param meetingType - The type of meeting being joined
 * @param clientId - The id for the client partition
 * @returns Promise resolving to the join response data
 */
const sendJoinRequest = async (
    authData: CustomAuthoriserData,
    meetingType: MeetingJoinType,
): Promise<any> => {

    const token = createCustomToken(authData)

    if (!token) {
        console.error("~~Join Chime Meeting incomplete parameters")
        throw new Error("Missing Authentication")
    }

    const url = getUrl("chime", authData.clientId, "join")

    let joinTypeString = "Web"

    if (meetingType === MeetingJoinType.Headset || meetingType === MeetingJoinType.Phone) {
        joinTypeString = meetingType
    }

    const body = {
        meetingId: authData.meetingId,
        attendeeId: authData.userId,
        meetingType: joinTypeString,
    }

    let response = null;
    try {
        response = await fetch(url, {
            method: "POST",
            body: JSON.stringify(body),
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
        });
    }
    catch (error) {
        console.log(error);
        return null;
    }

    let json
    try {
        json = await response.json()
    } catch (error: any) {
        console.log("Error getting response", error)
        throw new Error("Error (500): Failed to join meeting")
    }

    if (!response.ok) {
        throw new Error(`Error (${response.status}): ${json.message}`);
    }

    return json
};

export interface JoinResponse {
    fullName: string;
    jobTitle: string;
    joinCode: string;
    joinNumber: number;
}

/**
 * Joins a meeting using the Chime MeetingManager
 * @param meetingManager - The Chime MeetingManager instance
 * @param meetingId - The id of the meeting to join
 * @param attendeeId - The id of the attendee joining the meeting
 * @param meetingType - The type of meeting being joined
 * @returns Promise resolving to the join response data or null on failure
 */
export const joinMeeting = async (
    meetingManager: MeetingManager,
    authData: CustomAuthoriserData,
    meetingType: MeetingJoinType
): Promise<JoinResponse | null> => {

    const response = await sendJoinRequest(
        authData,
        meetingType,
    );

    if (!response) {
        console.log("Error joining meeting")
        return null;
    }

    meetingManager.getAttendee = getAttendee;

    const meetingSessionConfiguration = new MeetingSessionConfiguration(
        response.JoinInfo.Meeting,
        response.JoinInfo.Attendee
    );

    let deviceLabels = DeviceLabels.AudioAndVideo;

    switch (response.deviceLabels) {
        case "Video":
            deviceLabels = DeviceLabels.Video;
            break;
        case "Audio":
            deviceLabels = DeviceLabels.Audio;
            break;
        case "AudioAndVideo":
            deviceLabels = DeviceLabels.AudioAndVideo;
            break;
        default:
            deviceLabels = DeviceLabels.None;
            break;
    }

    const options = {
        deviceLabels: deviceLabels,
    };

    // Use the join API to create a meeting session using the above data
    await meetingManager.join(meetingSessionConfiguration, options);

    // Start the session to join the meeting
    await meetingManager.start();

    let joinNumber = -1

    if (response.joinNumber != undefined && Number.isInteger(response.joinNumber)) {
        joinNumber = response.joinNumber
    }

    return {
        fullName: response?.fullName || "",
        jobTitle: response?.jobTitle || "",
        joinCode: response?.joinCode || "",
        joinNumber,
    }
};

const getAttendee = (
    chimeAttendeeId: string,
    externalUserId?: string | undefined
): Promise<AttendeeResponse> => {

    if (!externalUserId) {
        return Promise.resolve({
            name: "???",
            actualUserId: chimeAttendeeId,
            userType: "",
        });
    }

    const results = getMeetingStatusFromExternalId(externalUserId);

    return Promise.resolve(results);
}

export const getMeetingStatusFromExternalId = (
    externalUserId: string
) => {

    const split = externalUserId.split("#");

    if (split.length < 2) {
        return {
            name: externalUserId,
            actualUserId: externalUserId,
            userType: "",
        };
    }

    if (split.length < 3) {
        return {
            name: split[1],
            actualUserId: split[0],
            userType: "",
        }
    }

    let userType = split[2] || "";

    if (userType.length > 0) {
        let found = ""
        for (const type of Object.values(MeetingJoinType)) {
            if (type.includes(userType)) {
                found = type;
                break;
            }
        }

        userType = found;
    }

    return {
        name: split[1],
        actualUserId: split[0],
        userType: userType,
    };
}